<template lang="pug">
div
  v-layout.header-title(align-center justify-start row fill-height)
    action-title-bar.title-bar(is-inline hide-subtitle :title='selectedOption.text')
      span.ml-2.title-complement {{ subtitle }}
  v-layout.mt-4(fluid align-center)
    v-menu(offset-y)
      v-toolbar-title.liondesk(slot='activator')
        span.font-weight-regular {{ selectedOption.text }}
        v-icon(color='primary', right) arrow_drop_down
      v-list
        v-list-tile(
          v-for='option in options'
          :key='option.value'
          @click='selectReportOption(option)'
        ) 
          v-list-tile-title
            | {{ option.text }}
  v-layout.mt-4(fluid)
    v-card.flex
      v-container.pa-0(fluid, :class='{ "in-progress": importReportsLoading }')
        v-data-table.lonewolf-table(
          hide-actions
          :headers='reportingHeaders'
          :items='importReports'
          :loading='importReportsLoading'
        )
          template(slot="headerCell" slot-scope="props")
              span {{ props.header.text }}
                v-tooltip(v-if="showHeaderTooltip(props.header)" top)
                  span {{ props.header.toolTipText }}
                  template(slot="activator")
                    v-icon.pl-2.tooltip-icon(flat) help_outline
          template(slot="items" slot-scope="data")
            tr
              td
                span {{ data.item.file_name }}
              td(v-if="selectedOption.isArchivedOption") {{ data.item.contacts_total }}
              td(v-else)
                a(@click="openContactsModal(data.item)") {{ data.item.contacts_total }}
              td.text-xs-center
                import-contacts-status-field(:status="data.item.status")
              td.text-xs-center 
                span(:class="getStatusStyle(data.item.status)") {{ data.item.successful_total }} / {{ data.item.partial_total }} / {{ data.item.failed_total }}
              td(v-if="showDateTime(data.item)") {{ data.item.created_at | formatDateTime }}
              td(v-else) {{ data.item.created_at | formatDate }}
          template(slot="no-data")
            v-layout.mb-4.mt-3.py-4.pr-4(column align-center)
              v-flex.mb-2
                v-icon(size=40) info
              v-flex
                span.title
                  | No Import Contacts created yet
              v-flex
                span.subheading Your Import Contacts will be shown here.
          template(v-if="hasImportContactsLeftToFetch" slot="footer")
            scroll-observer(@intersecting="infiniteScroll")
  contacts-report-modal(
    ref="contactsModal"
    :master-id="currentReport.master_id"
    :date-created="currentReport.created_at"
    :hide-partial-tab="!currentReport.partial_total"
    :reimport-file="!!currentReport.reimport_file_id"
  )
  liondesk-dialog(
    width="600px"
    v-model="showNewImportDialog"
    dialog-title="IMPORT HISTORY"
    hide-ok-button
    cancel-button-text="Close"
    @click:cancel="handleCloseNewImportDialog"
  )
    template(slot="body")
      v-container.px-0.py-0
        v-layout(row wrap)
          v-flex(xs12)
            span.font-weight-bold.new-import-text We're working on importing your contacts!
          v-flex(xs12)
            span The import might take up to 24 hours based on the number of contact records.
          v-flex.my-3(xs12)
            span Once everything's set, you'll see two things:
          v-flex.mb-3(xs12)
            ul
              li A red dot appear on the bell icon in the top right corner.
              li An icon pop up on the import button on the contacts page.
          v-flex(xs12)
            span In the meantime, explore other areas of LionDesk. 
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import LiondeskDialog from '@/Core/components/LiondeskDialog';
import ActionTitleBar from '@/Core/components/ActionTitleBar';
import ScrollObserver from '@/Core/components/ScrollObserver';
import { formatDate, formatDateTime } from '@/Core/filters/dates';
import {
  importContactsReportOption,
  headers,
  FAILED_STATUS,
  PENDING_STATUS,
} from '@/Contacts/components/import/config/import-contacts-reports';
import { LS_PROCESSING_DATE } from '@/Contacts/components/import/config/new-flow.js';

import ImportContactsStatusField from './ImportContactsStatusField';
import ContactsReportModal from './ContactsReportModal';

const {
  mapActions: mapImportContactsReportActions,
  mapState: mapImportContactsReportState,
  mapGetters: mapImportContactsReportGetters,
} = createNamespacedHelpers('importContactsReport');

const { mapActions: mapContactsImportActions, mapState: mapContactsImportState } = createNamespacedHelpers('contactsImport');

export default {
  name: 'ImportContactsReports',
  filters: {
    formatDate,
    formatDateTime,
  },
  components: {
    ActionTitleBar,
    ScrollObserver,
    ImportContactsStatusField,
    ContactsReportModal,
    LiondeskDialog,
  },
  data() {
    return {
      reportingHeaders: headers,
      selectedOption: importContactsReportOption[0],
      options: importContactsReportOption,
      pagination: { page: 1 },
      currentReport: {},
      showNewImportDialog: false,
    };
  },
  computed: {
    ...mapImportContactsReportState([
      'importReports',
      'importReportsTotal',
      'importReportsLoading',
    ]),
    ...mapImportContactsReportGetters(['hasImportContactsLeftToFetch']),
    ...mapContactsImportState(['hasFirstImportSession']),
    subtitle() {
      return !this.selectedOption.isArchivedOption ? '(past 30 days)' : '(older than 30 days)';
    },
  },
  mounted() {
    this.loadImportReports();
  },
  methods: {
    ...mapImportContactsReportActions(['findImportReports', 'findContactsReports']),
    ...mapContactsImportActions(['trackInProgressImportProcess', 'isFirstImportOnCurrentSession']),
    showHeaderTooltip(header) {
      if (this.selectedOption.isArchivedOption) {
        return false;
      }
      return header.showTooltip;
    },
    selectReportOption(selectedOption) {
      this.selectedOption = selectedOption;
      this.refreshTable();
    },
    refreshTable() {
      this.pagination.page = 1;
      this.loadImportReports();
    },
    loadImportReports() {
      this.findImportReports({
        archived: this.selectedOption.isArchivedOption,
        pagination: this.pagination,
      });
    },
    getStatusStyle(status) {
      switch (status) {
        case FAILED_STATUS:
          return 'percentage-text-error';
        case PENDING_STATUS:
          return 'percentage-text-pending';
        default:
          return 'percentage-text';
      }
    },
    infiniteScroll() {
      this.pagination.page++;
      if (this.hasImportContactsLeftToFetch) {
        this.loadImportReports();
      }
    },
    openContactsModal(report) {
      this.currentReport = report;
      this.$refs.contactsModal.modal_open();
    },
    updateInProgressProcessTracker() {
      if (!this.importReports.length || this.selectedOption.isArchivedOption) return;

      const trackDate = localStorage.getItem(LS_PROCESSING_DATE);
      if (!trackDate) {
        const report = this.importReports
          .toReversed()
          .find(report => report.status === PENDING_STATUS);
        if (!report) return;

        const newTrackDate = Date.parse(report.created_at);
        this.trackInProgressImportProcess(newTrackDate);
        return;
      }

      const inProcessReports = this.importReports
        .toReversed()
        .filter(
          report => trackDate <= Date.parse(report.created_at) && report.status === PENDING_STATUS,
        );
      if (inProcessReports.length) {
        const olderReport = inProcessReports[0];
        this.trackInProgressImportProcess(Date.parse(olderReport.created_at));
        return;
      }

      const olderReport = this.importReports[0];
      this.trackInProgressImportProcess(Date.parse(olderReport.created_at) + 1);
    },
    handleCloseNewImportDialog() {
      this.showNewImportDialog = false;
    },
    showDateTime(report) {
      return this.importReports.some(record => {
        if (report.master_id === record.master_id) {
          return false;
        }

        if (record.file_name !== report.file_name) {
          return false;
        }

        return record.created_at.substring(0,10) === report.created_at.substring(0,10);
      });
    },
    openDialogFirstImport() {
      if (this.hasFirstImportSession || this.selectedOption.isArchivedOption) {
        return;
      }

      this.showNewImportDialog = this.$route.query['new-import'] === 'true';
      

      if (this.showNewImportDialog) {
        this.$router.replace({'query': null});
        this.isFirstImportOnCurrentSession();
      }
    },
  },
  watch: {
    importReports(reports) {
      this.updateInProgressProcessTracker();
      this.openDialogFirstImport();
    },
  },
};
</script>

<style lang="scss" scoped>
.title-complement {
  font-size: 20px !important;
  font-weight: 500;
}
.new-import-text {
  font-size: 16px;
}

.total-description {
  color: var(--v-text-lighten3);
}

.percentage-text {
  color: var(--v-text-lighten3);
}

.percentage-text-pending {
  color: var(--v-primary-lighten2);
}

.percentage-text-error {
  color: red;
}
.tooltip-icon {
  vertical-align: middle;
}
</style>
